import type { SupportedLanguage } from '@/i18n/config';
import type { Meeting } from './meeting.service';
import type { Topico } from './topico.service';
import type { ItemAta } from './itemAta.service';
import type { Desenvolvimento } from './desenvolvimento.service';
import type { MapaAtasResponse } from '../../types/database';

export interface OrganizedDataForTranslation {
  topicos: Array<Topico & { items: Array<ItemAta & { desenvolvimentos: Desenvolvimento[] }> }>;
  itemsSemTopico: Array<ItemAta & { desenvolvimentos: Desenvolvimento[] }>;
}

export interface TranslatedMeetingData {
  meeting: Meeting;
  organizedData: OrganizedDataForTranslation;
}

const LANG_CODE_MAP: Record<SupportedLanguage, string> = {
  'pt-BR': 'pt',
  'pt-PT': 'pt-PT',
  'en': 'en',
  'es': 'es',
};

const GOOGLE_TRANSLATE_API = 'https://translate.googleapis.com/translate_a/single';
const MAX_CHARS_PER_REQUEST = 4500;
const CONCURRENCY_LIMIT = 6;

const translationCache = new Map<string, string>();

export function estimateTranslationChars(
  meeting: Meeting,
  organizedData: OrganizedDataForTranslation
): number {
  let total = 0;
  if (meeting.name) total += meeting.name.length;
  if (meeting.location) total += meeting.location.length;
  for (const topico of organizedData.topicos) {
    total += topico.titulo.length;
    for (const item of topico.items) {
      if (item.descricao) total += item.descricao.length;
      for (const dev of item.desenvolvimentos) {
        if (dev.texto) total += dev.texto.length;
      }
    }
  }
  for (const item of organizedData.itemsSemTopico) {
    if (item.descricao) total += item.descricao.length;
    for (const dev of item.desenvolvimentos) {
      if (dev.texto) total += dev.texto.length;
    }
  }
  return total;
}

function splitIntoChunks(text: string): string[] {
  if (text.length <= MAX_CHARS_PER_REQUEST) return [text];

  const chunks: string[] = [];
  const paragraphs = text.split(/\n\n+/);
  let current = '';

  for (const paragraph of paragraphs) {
    if ((current + (current ? '\n\n' : '') + paragraph).length <= MAX_CHARS_PER_REQUEST) {
      current += (current ? '\n\n' : '') + paragraph;
    } else {
      if (current) {
        chunks.push(current.trim());
        current = '';
      }
      if (paragraph.length > MAX_CHARS_PER_REQUEST) {
        const sentences = paragraph.split(/(?<=[.!?])\s+/);
        for (const sentence of sentences) {
          if ((current + (current ? ' ' : '') + sentence).length <= MAX_CHARS_PER_REQUEST) {
            current += (current ? ' ' : '') + sentence;
          } else {
            if (current) chunks.push(current.trim());
            if (sentence.length > MAX_CHARS_PER_REQUEST) {
              const words = sentence.split(' ');
              current = '';
              for (const word of words) {
                if ((current + ' ' + word).length <= MAX_CHARS_PER_REQUEST) {
                  current += (current ? ' ' : '') + word;
                } else {
                  if (current) chunks.push(current.trim());
                  current = word;
                }
              }
            } else {
              current = sentence;
            }
          }
        }
      } else {
        current = paragraph;
      }
    }
  }

  if (current) chunks.push(current.trim());
  return chunks.filter(Boolean);
}

async function translateSingleText(text: string, to: string): Promise<string> {
  if (!text?.trim()) return text;

  const cacheKey = `${to}|${text}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }

  const chunks = splitIntoChunks(text);

  const translatedChunks = await Promise.all(
    chunks.map(async (chunk) => {
      const chunkKey = `${to}|${chunk}`;
      if (translationCache.has(chunkKey)) {
        return translationCache.get(chunkKey)!;
      }
      try {
        const params = new URLSearchParams({
          client: 'gtx',
          sl: 'auto',
          tl: to,
          dt: 't',
          q: chunk,
        });
        const response = await fetch(`${GOOGLE_TRANSLATE_API}?${params}`);

        if (!response.ok) return chunk;

        const data = await response.json();

        if (Array.isArray(data?.[0])) {
          const translated = (data[0] as Array<[string]>).map((item) => item[0]).join('');
          translationCache.set(chunkKey, translated);
          return translated;
        }
        return chunk;
      } catch {
        return chunk;
      }
    })
  );

  const result = translatedChunks.join('\n\n');
  translationCache.set(cacheKey, result);
  return result;
}

async function translateBatch(
  texts: string[],
  to: string,
  onProgress?: (current: number, total: number) => void
): Promise<string[]> {
  const results: string[] = new Array(texts.length).fill('');
  let completed = 0;

  for (let i = 0; i < texts.length; i += CONCURRENCY_LIMIT) {
    const batch = texts.slice(i, i + CONCURRENCY_LIMIT);
    const batchResults = await Promise.all(
      batch.map(async (text) => {
        const result = await translateSingleText(text, to);
        completed++;
        onProgress?.(completed, texts.length);
        return result;
      })
    );
    for (let j = 0; j < batchResults.length; j++) {
      results[i + j] = batchResults[j];
    }
  }

  return results;
}

export async function translateMeetingData(
  meeting: Meeting,
  organizedData: OrganizedDataForTranslation,
  fromLanguage: SupportedLanguage,
  toLanguage: SupportedLanguage,
  onProgress?: (current: number, total: number) => void
): Promise<TranslatedMeetingData> {
  const from = LANG_CODE_MAP[fromLanguage] ?? 'pt';
  const to = LANG_CODE_MAP[toLanguage] ?? 'en';

  if (from === to) {
    return { meeting, organizedData };
  }

  interface TextEntry {
    key: string;
    text: string;
  }

  const entries: TextEntry[] = [];

  if (meeting.name) entries.push({ key: 'meeting_name', text: meeting.name });
  if (meeting.location) entries.push({ key: 'meeting_location', text: meeting.location });

  for (const topico of organizedData.topicos) {
    entries.push({ key: `topico_${topico.id}`, text: topico.titulo });

    for (const item of topico.items) {
      if (item.descricao) {
        entries.push({ key: `item_${item.id}`, text: item.descricao });
      }
      for (const dev of item.desenvolvimentos) {
        if (dev.texto) {
          entries.push({ key: `dev_${dev.id}`, text: dev.texto });
        }
      }
    }
  }

  for (const item of organizedData.itemsSemTopico) {
    if (item.descricao) {
      entries.push({ key: `item_${item.id}`, text: item.descricao });
    }
    for (const dev of item.desenvolvimentos) {
      if (dev.texto) {
        entries.push({ key: `dev_${dev.id}`, text: dev.texto });
      }
    }
  }

  const textsToTranslate = entries.map((e) => e.text);
  const translatedTexts = await translateBatch(textsToTranslate, to, onProgress);

  const translationMap = new Map<string, string>();
  for (let i = 0; i < entries.length; i++) {
    translationMap.set(entries[i].key, translatedTexts[i]);
  }

  const translatedMeeting: Meeting = {
    ...meeting,
    name: translationMap.get('meeting_name') ?? meeting.name,
    location: translationMap.get('meeting_location') ?? meeting.location,
  };

  const translatedTopicos = organizedData.topicos.map((topico) => ({
    ...topico,
    titulo: translationMap.get(`topico_${topico.id}`) ?? topico.titulo,
    items: topico.items.map((item) => ({
      ...item,
      descricao: translationMap.get(`item_${item.id}`) ?? item.descricao,
      desenvolvimentos: item.desenvolvimentos.map((dev) => ({
        ...dev,
        texto: translationMap.get(`dev_${dev.id}`) ?? dev.texto,
      })),
    })),
  }));

  const translatedItemsSemTopico = organizedData.itemsSemTopico.map((item) => ({
    ...item,
    descricao: translationMap.get(`item_${item.id}`) ?? item.descricao,
    desenvolvimentos: item.desenvolvimentos.map((dev) => ({
      ...dev,
      texto: translationMap.get(`dev_${dev.id}`) ?? dev.texto,
    })),
  }));

  return {
    meeting: translatedMeeting,
    organizedData: {
      topicos: translatedTopicos,
      itemsSemTopico: translatedItemsSemTopico,
    },
  };
}

export async function translateMapaAtasData(
  data: MapaAtasResponse,
  fromLanguage: SupportedLanguage,
  toLanguage: SupportedLanguage,
  onProgress?: (current: number, total: number) => void
): Promise<MapaAtasResponse> {
  const from = LANG_CODE_MAP[fromLanguage] ?? 'pt';
  const to = LANG_CODE_MAP[toLanguage] ?? 'en';

  if (from === to) return data;

  interface TextEntry {
    key: string;
    text: string;
  }

  const entries: TextEntry[] = [];

  for (const topico of data.topicos) {
    entries.push({ key: `topico_${topico.id}`, text: topico.titulo });
    for (const item of topico.itens) {
      if (item.descricao) entries.push({ key: `item_${item.id}`, text: item.descricao });
      for (let i = 0; i < item.desenvolvimentos.length; i++) {
        const dev = item.desenvolvimentos[i];
        if (dev.texto) entries.push({ key: `dev_${item.id}_${i}`, text: dev.texto });
      }
    }
  }

  for (const item of data.itensSemTopico) {
    if (item.descricao) entries.push({ key: `item_${item.id}`, text: item.descricao });
    for (let i = 0; i < item.desenvolvimentos.length; i++) {
      const dev = item.desenvolvimentos[i];
      if (dev.texto) entries.push({ key: `dev_${item.id}_${i}`, text: dev.texto });
    }
  }

  const textsToTranslate = entries.map((e) => e.text);
  const translatedTexts = await translateBatch(textsToTranslate, to, onProgress);

  const translationMap = new Map<string, string>();
  for (let i = 0; i < entries.length; i++) {
    translationMap.set(entries[i].key, translatedTexts[i]);
  }

  return {
    ...data,
    topicos: data.topicos.map((topico) => ({
      ...topico,
      titulo: translationMap.get(`topico_${topico.id}`) ?? topico.titulo,
      itens: topico.itens.map((item) => ({
        ...item,
        descricao: translationMap.get(`item_${item.id}`) ?? item.descricao,
        desenvolvimentos: item.desenvolvimentos.map((dev, i) => ({
          ...dev,
          texto: translationMap.get(`dev_${item.id}_${i}`) ?? dev.texto,
        })),
      })),
    })),
    itensSemTopico: data.itensSemTopico.map((item) => ({
      ...item,
      descricao: translationMap.get(`item_${item.id}`) ?? item.descricao,
      desenvolvimentos: item.desenvolvimentos.map((dev, i) => ({
        ...dev,
        texto: translationMap.get(`dev_${item.id}_${i}`) ?? dev.texto,
      })),
    })),
  };
}